import {FC, KeyboardEvent, useEffect, useId, useRef, useState} from 'react'
import {isModPressed} from '../keyboard/platform'
import {clipboardService} from '../service/clipboardService'
import {downloadMarkdown} from '../service/markdownExportUtils'
import {MarkdownViewer} from './MarkdownViewer'
import './ExportMarkdownDialog.less'

interface Props {
    markdown: string | null
    filename: string
    onClose: () => void
}

const isInteractiveTarget = (target: EventTarget | null): boolean =>
    target instanceof Element && target.closest('button, a, input, textarea, select, [contenteditable="true"]') !== null

const hasTextSelection = (): boolean => (window.getSelection()?.toString() ?? '').length > 0

export const ExportMarkdownDialog: FC<Props> = ({markdown, filename, onClose}) => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const titleId = useId()
    const [viewMode, setViewMode] = useState<'preview' | 'raw'>('preview')

    useEffect(() => {
        const dialog = dialogRef.current
        dialog?.showModal()
        // Focus the dialog itself instead of the first tab button, so Enter triggers Download
        dialog?.focus()
        return () => {
            dialog?.close()
        }
    }, [])

    const handleDownload = () => {
        if (markdown === null) {
            return
        }
        downloadMarkdown(filename, markdown)
    }

    const handleCopy = () => {
        if (markdown === null) {
            return
        }
        clipboardService.copyTo(markdown, false)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
        if (event.code === 'Escape') {
            // Prevent the native dialog cancel event so onClose is called only once
            event.preventDefault()
            onClose()
            return
        }
        if (event.code === 'Enter' || event.code === 'NumpadEnter') {
            // Focused buttons and links keep their native Enter behaviour;
            // only a plain, non-repeating keypress triggers the download
            if (isInteractiveTarget(event.target)
                || event.repeat
                || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
                return
            }
            event.preventDefault()
            handleDownload()
            return
        }
        if (isModPressed(event) && event.code === 'KeyC') {
            // Let the browser copy a manual text selection (e.g. a fragment of the raw view)
            if (hasTextSelection()) {
                return
            }
            event.preventDefault()
            handleCopy()
        }
    }

    return (
        <dialog
            ref={dialogRef}
            className="export-markdown-dialog"
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            onCancel={event => {
                event.preventDefault()
                onClose()
            }}
        >
            <h3 id={titleId}>Export to Markdown</h3>
            <div className="export-markdown-dialog__tabs">
                <button type="button"
                        className={`export-markdown-dialog__tab ${viewMode === 'preview' ? 'export-markdown-dialog__tab--active' : ''}`}
                        onClick={() => setViewMode('preview')}>
                    Preview
                </button>
                <button type="button"
                        className={`export-markdown-dialog__tab ${viewMode === 'raw' ? 'export-markdown-dialog__tab--active' : ''}`}
                        onClick={() => setViewMode('raw')}>
                    Raw
                </button>
            </div>
            <div className="export-markdown-dialog__content">
                {markdown === null ? (
                    <div className="export-markdown-dialog__loading">Loading…</div>
                ) : viewMode === 'preview' ? (
                    <MarkdownViewer sourceCode={markdown} compactMode={false} onToggleCheckBox={() => {}}/>
                ) : (
                    <pre className="export-markdown-dialog__raw">{markdown}</pre>
                )}
            </div>
            <div className="buttons">
                <button type="button" onClick={onClose}>Close</button>
                <button type="button" onClick={handleCopy} disabled={markdown === null}>
                    Copy
                </button>
                <button type="button" className="download-button" onClick={handleDownload} disabled={markdown === null}>
                    Download
                </button>
            </div>
        </dialog>
    )
}
